import { get, writable } from "svelte/store";
import { io } from "socket.io-client";
import { createPeerConnection, handleAnswer, handleOffer, addIceCandidate, startLocalVideo, hangupPeerConnection, startScreensharingConseille, startShareScreen, } from "./peerConnectionStore";
import { getRoom, setRoom } from "./roomStore";
import { translation } from "../stores";
import { addToast } from "./toastStore";
import { roomStateStore } from "./roomStateStore";
import { closeCall } from "../utils/closeCall";
import { ROOM_CONNECTION_STATE } from "../types/Room/RoomConnectionState";
import { buildLog } from "../utils/log";
import { addIceCandidateWithExpert, createOfferWithExpert, createPeerConnectionWithExpert, handleAnswerWithExpert } from "./peerConnectionWithExpertStore";
import { addIceCandidateWithClient, createPeerConnectionWithClient, handleOfferWithClient } from "./peerConnectionWithClientStore";
export const socketStore = writable(null);
export function initSocket(roomId, options = {}) {
    console.log('[initSocket] roomId:', roomId, ' options:', options);
    return new Promise((resolve, reject) => {
        const { role, pin } = options;
        const socket = io('/', {
            query: {
                roomId,
                role: role,
            },
        });
        socket.on("connect", () => {
            console.log("[socket] connected", socket.id, ' in room:', roomId);
            socket.emit("message", {
                action: "subscribe",
                type: role,
                room: roomId,
                pin: pin || '',
            });
            buildLog({
                roomId,
                status: ROOM_CONNECTION_STATE.CONNECTING,
                page: "Camera",
                message: `${role} is connected to socket server and asked for subscription.`
            });
            socketStore.set(socket);
            resolve(socket);
        });
        socket.on("message", async (data) => {
            console.log("[socket] message", data);
            if (role === 'client') {
                handleClientMessage(data);
            }
            else if (role === 'expert') {
                handleExpertMessage(data);
            }
        });
        socket.on("disconnect", () => {
            console.log("[socket] disconnected");
            buildLog({
                roomId,
                status: ROOM_CONNECTION_STATE.DISCONNECTED,
                page: "Camera",
                message: "Client is disconnected"
            });
        });
        socket.on("connect_error", reject);
    });
}
async function handleClientMessage(data) {
    var _a, _b, _c;
    const roomId = Number(data.room);
    // const roomId = String(data.room)
    const sessionType = data.sessionType;
    const audioEnabled = data.audioEnabled === "Yes";
    switch (data.action) {
        case "JoinedRoom":
        case "CreatedRoom":
            if (roomId && sessionType) {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.CONNECTED,
                    page: "Camera",
                    message: `"Client is connected on camera page"`
                });
                setRoom(roomId, sessionType, audioEnabled);
                roomStateStore.set(ROOM_CONNECTION_STATE.CONNECTED);
                await createPeerConnection(roomId);
                if (sessionType === "video") {
                    startLocalVideo();
                }
                else if (sessionType === 'screensharing') {
                    startShareScreen();
                }
                else if (sessionType === 'screensharingConseille') {
                    startScreensharingConseille();
                }
                else {
                    buildLog({
                        roomId,
                        status: ROOM_CONNECTION_STATE.ERROR,
                        page: "Camera",
                        message: "Room configuration invalid or unsupported session type: " + sessionType,
                    });
                    addToast('error', translation.alert.roomError);
                    hangupPeerConnection();
                    roomStateStore.set(ROOM_CONNECTION_STATE.ERROR);
                }
            }
            else {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.ERROR,
                    page: "Camera",
                    message: "Room or SessionType empty",
                });
                addToast('error', translation.alert.roomError);
                hangupPeerConnection();
                roomStateStore.set(ROOM_CONNECTION_STATE.ERROR);
            }
            break;
        case "ICEOffer":
            await handleOffer(data.sdp);
            break;
        case "ICEAnswer":
            if (data.connectionType === "expert") {
                await handleAnswerWithExpert(data.sdp);
            }
            else {
                await handleAnswer(data.sdp);
            }
            break;
        case "ice":
            if (data.connectionType === "expert") {
                await addIceCandidateWithExpert(data);
            }
            else {
                await addIceCandidate(data);
            }
            break;
        case "Rejected":
            buildLog({
                roomId,
                status: ROOM_CONNECTION_STATE.REJECTED,
                page: "Camera",
                message: "Room is full or not exist"
            });
            addToast('error', translation.alert.sessionFullError);
            hangupPeerConnection();
            roomStateStore.set(ROOM_CONNECTION_STATE.REJECTED);
            break;
        case "ping":
            sendSocketMessage({ action: "pong" });
            break;
        case "hangup": {
            const roomId = (_a = getRoom()) === null || _a === void 0 ? void 0 : _a.id;
            if (roomId) {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.DISCONNECTED,
                    page: "Camera",
                    message: "Room closed by remote"
                });
            }
            closeCall(roomId);
            break;
        }
        case "offline": {
            const roomId = (_b = getRoom()) === null || _b === void 0 ? void 0 : _b.id;
            if (roomId) {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.DISCONNECTED,
                    page: "Camera",
                    message: "Room closed by offline"
                });
            }
            closeCall(roomId);
            break;
        }
        case "ExpertJoined": {
            const roomId = (_c = getRoom()) === null || _c === void 0 ? void 0 : _c.id;
            await createPeerConnectionWithExpert(roomId);
            await createOfferWithExpert();
            break;
        }
    }
}
async function handleExpertMessage(data) {
    var _a, _b;
    const roomId = Number(data.room);
    const sessionType = data.sessionType;
    const audioEnabled = data.audioEnabled === "Yes";
    switch (data.action) {
        case "ExpertJoinedRoom":
            if (roomId && sessionType) {
                buildLog({
                    roomId: roomId,
                    status: ROOM_CONNECTION_STATE.CONNECTED,
                    page: "Camera",
                    message: "Expert is connected on camera page"
                });
                setRoom(roomId, sessionType, audioEnabled);
                roomStateStore.set(ROOM_CONNECTION_STATE.CONNECTED);
            }
            else {
                buildLog({
                    roomId: roomId,
                    status: ROOM_CONNECTION_STATE.ERROR,
                    page: "Camera",
                    message: "Room or SessionType empty"
                });
                addToast('error', translation.alert.roomError);
                hangupPeerConnection();
                roomStateStore.set(ROOM_CONNECTION_STATE.ERROR);
            }
            break;
        case "JoinedRoom":
        case "ICEOffer":
            await createPeerConnectionWithClient(roomId);
            await handleOfferWithClient(data.sdp);
            break;
        case "ice":
            // await addIceCandidate(data);
            await addIceCandidateWithClient(data);
            break;
        case "Rejected":
            addToast('error', data.error);
            buildLog({
                roomId: roomId,
                status: ROOM_CONNECTION_STATE.REJECTED,
                page: "Camera",
                message: data.error
            });
            // addToast('error', translation.alert.sessionFullError)
            hangupPeerConnection();
            roomStateStore.set(ROOM_CONNECTION_STATE.REJECTED);
            break;
        case "ping":
            sendSocketMessage({ action: "pong" });
            break;
        case "hangup": {
            const roomId = (_a = getRoom()) === null || _a === void 0 ? void 0 : _a.id;
            if (roomId) {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.DISCONNECTED,
                    page: "Camera",
                    message: "Room closed by remote"
                });
            }
            closeCall(roomId);
            break;
        }
        case "offline": {
            const roomId = (_b = getRoom()) === null || _b === void 0 ? void 0 : _b.id;
            if (roomId) {
                buildLog({
                    roomId,
                    status: ROOM_CONNECTION_STATE.DISCONNECTED,
                    page: "Camera",
                    message: "Room closed by offline"
                });
            }
            closeCall(roomId);
            break;
        }
    }
}
export function closeSocket(roomId) {
    const socket = get(socketStore);
    if (socket && socket.connected) {
        sendSocketMessage({
            action: "hangup",
            room: roomId
        });
        socket.disconnect();
        socketStore.set(null);
        console.log("[closeSocket] disconnected after hangup sent");
    }
}
export function sendSocketMessage(data) {
    const socket = get(socketStore);
    if (!socket || !socket.connected)
        return;
    socket.emit("message", data);
    console.log('[sendSocketMessage] data sent:', data);
}
